import { useCallback, useEffect, useMemo, useState } from 'react'
import { Button, Empty, Input, Modal, Radio, Spin, message } from 'antd'
import { TeamOutlined } from '@ant-design/icons'
import { adminApi, formatApiError, type PageResult } from '../../api'
import { BROWSE_PICKER_Z_INDEX } from './ObjectBrowseField'
import './RolePickerModal.css'

export type RolePickValue = {
  id: number
  name: string
}

type RoleRow = {
  id: number
  name: string
  description?: string
}

type Props = {
  open: boolean
  value?: RolePickValue | null
  onCancel: () => void
  onConfirm: (value: RolePickValue | null) => void
}

export function RolePickerModal({ open, value, onCancel, onConfirm }: Props) {
  const [rows, setRows] = useState<RoleRow[]>([])
  const [loading, setLoading] = useState(false)
  const [keyword, setKeyword] = useState('')
  const [selectedId, setSelectedId] = useState<number | null>(value?.id ?? null)

  const loadRoles = useCallback(() => {
    setLoading(true)
    adminApi
      .get<PageResult<RoleRow>>('/admin/roles', { params: { page: 1, page_size: 100 } })
      .then((r) => setRows(r.data.items || []))
      .catch((e) => message.error(formatApiError(e, '加载角色失败')))
      .finally(() => setLoading(false))
  }, [])

  useEffect(() => {
    if (!open) return
    setKeyword('')
    setSelectedId(value?.id ?? null)
    loadRoles()
  }, [open, value, loadRoles])

  const filtered = useMemo(() => {
    const kw = keyword.trim().toLowerCase()
    if (!kw) return rows
    return rows.filter(
      (r) =>
        r.name.toLowerCase().includes(kw) ||
        (r.description || '').toLowerCase().includes(kw),
    )
  }, [rows, keyword])

  const handleConfirm = () => {
    if (selectedId == null) {
      message.warning('请选择角色')
      return
    }
    const hit = rows.find((r) => r.id === selectedId)
    onConfirm({
      id: selectedId,
      name: hit?.name || value?.name || `角色#${selectedId}`,
    })
  }

  return (
    <Modal
      className="role-picker-modal"
      title={
        <span className="role-picker-title">
          <TeamOutlined />
          选择角色
        </span>
      }
      open={open}
      onCancel={onCancel}
      cancelText="取消"
      width={480}
      zIndex={BROWSE_PICKER_Z_INDEX}
      destroyOnClose
      centered
      footer={
        <div className="role-picker-footer">
          <Button onClick={onCancel}>取消</Button>
          <Button type="primary" onClick={handleConfirm}>
            确定
          </Button>
        </div>
      }
    >
      <Input.Search
        allowClear
        placeholder="搜索角色名称"
        value={keyword}
        onChange={(e) => setKeyword(e.target.value)}
        className="role-picker-search"
      />
      <Spin spinning={loading}>
        <div className="role-picker-list">
          {filtered.length ? (
            <Radio.Group
              value={selectedId ?? undefined}
              onChange={(e) => setSelectedId(Number(e.target.value))}
              className="role-picker-radio-group"
            >
              {filtered.map((row) => (
                <Radio key={row.id} value={row.id} className="role-picker-row">
                  <span className="role-picker-row-name">{row.name}</span>
                  {row.description ? (
                    <span className="role-picker-row-desc">{row.description}</span>
                  ) : null}
                </Radio>
              ))}
            </Radio.Group>
          ) : (
            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="暂无角色" />
          )}
        </div>
      </Spin>
    </Modal>
  )
}
